/**
 * keyboard-shortcuts.ts - Keyboard Shortcuts for Result Entry
 *
 * Handles keyboard shortcuts (1-6) for quick result entry
 * and managing active match selection.
 */

import { submitResult } from './result-handler';

// State management for keyboard shortcuts
let isKeyboardShortcutsEnabled = false;
let currentActiveMatchId: string | null = null;
let currentTournamentId: string | null = null;

// Board number selection state
let boardNumberBuffer = '';
let boardNumberTimeout: ReturnType<typeof setTimeout> | null = null;
const BOARD_NUMBER_TIMEOUT_MS = 500; // Time to wait before processing input
let isBoardSelectionMode = false; // True when 'b' was pressed to enter board selection mode

/**
 * Result mappings for keyboard shortcuts
 * 1: White wins (1-0)
 * 2: Draw (0.5-0.5)
 * 3: Black wins (0-1)
 * 4: White wins FF (1-0ff)
 * 5: Both lose FF (0-0)
 * 6: Black wins FF (0-1ff)
 */
const KEYBOARD_RESULT_MAP: Record<string, string> = {
  '1': '1-0',      // White wins
  '2': '0.5-0.5',  // Draw
  '3': '0-1',      // Black wins
  '4': '1-0ff',    // White wins FF
  '5': '0-0',      // Both lose FF
  '6': '0-1ff',    // Black wins FF
};

/**
 * Initialize keyboard shortcuts for a tournament
 * @param tournamentId - The tournament ID
 */
export function initKeyboardShortcuts(tournamentId: string): void {
  currentTournamentId = tournamentId;
  isKeyboardShortcutsEnabled = true;

  // Select the first match without a result as active
  selectFirstEmptyMatch(tournamentId);

  // Add keyboard event listener if not already added
  if (!document.body.hasAttribute('data-keyboard-shortcuts-initialized')) {
    document.addEventListener('keydown', handleKeyboardShortcut);
    document.body.setAttribute('data-keyboard-shortcuts-initialized', 'true');
  }
}

/**
 * Disable keyboard shortcuts
 */
export function disableKeyboardShortcuts(): void {
  isKeyboardShortcutsEnabled = false;
  currentActiveMatchId = null;

  // Clear board number buffer state and remove visual feedback
  boardNumberBuffer = '';
  isBoardSelectionMode = false;
  if (boardNumberTimeout) {
    clearTimeout(boardNumberTimeout);
    boardNumberTimeout = null;
  }

  // Remove board selection mode highlight from all tables
  document.querySelectorAll('.matches-table.board-selection-mode').forEach(table => {
    table.classList.remove('board-selection-mode');
  });

  currentTournamentId = null;
}

/**
 * Handle keyboard shortcut events
 * @param event - Keyboard event
 */
function handleKeyboardShortcut(event: KeyboardEvent): void {
  // Only handle if shortcuts are enabled
  if (!isKeyboardShortcutsEnabled) {
    return;
  }

  // Don't trigger if user is typing in an input field
  // Exception: Allow shortcuts when result-select dropdown is focused
  const target = event.target as HTMLElement;
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
    return;
  }

  // Block shortcuts in SELECT elements UNLESS it's a result-select dropdown
  if (target.tagName === 'SELECT' && !target.classList.contains('result-select')) {
    return;
  }

  const key = event.key;

  // Handle 'b' key to toggle board selection mode
  if (key === 'b' || key === 'B') {
    event.preventDefault();
    if (isBoardSelectionMode) {
      exitBoardSelectionMode();
    } else {
      enterBoardSelectionMode();
    }
    return;
  }

  // Handle Escape to exit board selection mode or clear active match
  if (key === 'Escape') {
    event.preventDefault();
    if (isBoardSelectionMode) {
      exitBoardSelectionMode();
    } else {
      // Clear active match selection
      currentActiveMatchId = null;
      removeAllActiveHighlights();
    }
    return;
  }

  // Handle digit keys (0-9) for board selection or result entry
  if (/^[0-9]$/.test(key)) {
    event.preventDefault();
    handleDigitInput(key);
    return;
  }

  // Handle arrow keys for navigation (only if there's an active match)
  if (currentActiveMatchId && (key === 'ArrowDown' || key === 'ArrowUp')) {
    event.preventDefault();
    if (key === 'ArrowDown') {
      selectNextMatch();
    } else {
      selectPreviousMatch();
    }
  }
}

/**
 * Enter board selection mode (triggered by 'b' key)
 */
function enterBoardSelectionMode(): void {
  isBoardSelectionMode = true;
  boardNumberBuffer = '';
  if (boardNumberTimeout) {
    clearTimeout(boardNumberTimeout);
    boardNumberTimeout = null;
  }

  // Add visual feedback - highlight board number column
  if (currentTournamentId) {
    const pairingsTab = document.getElementById(`pairings-tab-${currentTournamentId}`);
    if (pairingsTab) {
      const tables = pairingsTab.querySelectorAll('.matches-table');
      tables.forEach(table => table.classList.add('board-selection-mode'));
    }
  }
}

/**
 * Exit board selection mode
 */
function exitBoardSelectionMode(): void {
  isBoardSelectionMode = false;
  boardNumberBuffer = '';
  if (boardNumberTimeout) {
    clearTimeout(boardNumberTimeout);
    boardNumberTimeout = null;
  }

  // Remove visual feedback
  document.querySelectorAll('.matches-table.board-selection-mode').forEach(table => {
    table.classList.remove('board-selection-mode');
  });
}

/**
 * Handle digit input for board selection or result entry
 * @param digit - The digit pressed (0-9)
 */
function handleDigitInput(digit: string): void {
  // Clear any existing timeout
  if (boardNumberTimeout) {
    clearTimeout(boardNumberTimeout);
    boardNumberTimeout = null;
  }

  // Add digit to buffer
  boardNumberBuffer += digit;

  const bufferNum = parseInt(boardNumberBuffer, 10);

  // In board selection mode (after pressing 'b'), all digits go to board selection
  if (isBoardSelectionMode) {
    // Multi-digit or single digit > 0: select board immediately for better responsiveness
    if (bufferNum > 0 && (boardNumberBuffer.length > 1 || bufferNum > 9)) {
      // Multi-digit number formed, select immediately
      selectBoardByNumber(bufferNum);
      exitBoardSelectionMode();
      return;
    }

    // Single digit: wait briefly for more digits
    boardNumberTimeout = setTimeout(() => {
      const num = parseInt(boardNumberBuffer, 10);
      if (num > 0) {
        selectBoardByNumber(num);
      }
      exitBoardSelectionMode();
    }, BOARD_NUMBER_TIMEOUT_MS);
    return;
  }

  // Not in board selection mode: use smart detection

  // Multi-digit number (like "15"): definitely board selection
  if (boardNumberBuffer.length > 1 && bufferNum > 0) {
    selectBoardByNumber(bufferNum);
    boardNumberBuffer = '';
    return;
  }

  // Single digit > 6: definitely board selection (no result mapping for 7-9)
  if (boardNumberBuffer.length === 1 && bufferNum > 6) {
    selectBoardByNumber(bufferNum);
    boardNumberBuffer = '';
    return;
  }

  // Single digit 0-6: wait to see if more digits follow
  // (0 by itself does nothing, but "01" selects board 1)
  boardNumberTimeout = setTimeout(() => {
    const num = parseInt(boardNumberBuffer, 10);
    boardNumberBuffer = '';

    // If we have an active match and it's 1-6, enter result
    if (currentActiveMatchId && num >= 1 && num <= 6) {
      const result = KEYBOARD_RESULT_MAP[num.toString()];
      if (result) {
        setMatchResult(currentActiveMatchId, result);
      }
    } else if (!currentActiveMatchId && num >= 1) {
      // No active match - select board by number
      selectBoardByNumber(num);
    }
    // num === 0 with no following digits: ignore (board 0 doesn't exist)
  }, BOARD_NUMBER_TIMEOUT_MS);
}

/**
 * Select a match by board number
 * @param boardNumber - The board number (1-indexed display number)
 */
function selectBoardByNumber(boardNumber: number): void {
  if (!currentTournamentId || boardNumber < 1) return;

  const pairingsTab = document.getElementById(`pairings-tab-${currentTournamentId}`);
  if (!pairingsTab) return;

  // Get all visible result selects in the currently visible round
  const selects = Array.from(pairingsTab.querySelectorAll('select.result-select')) as HTMLSelectElement[];

  // Filter to only visible matches and get them in display order
  const visibleSelects = selects.filter(select => {
    const row = select.closest('tr') as HTMLElement;
    return row && isElementVisible(row);
  });

  // Board numbers are 1-indexed in display
  const targetIndex = boardNumber - 1;

  if (targetIndex >= 0 && targetIndex < visibleSelects.length) {
    const targetSelect = visibleSelects[targetIndex]!;
    const matchId = targetSelect.id.replace('result-', '');
    setActiveMatch(matchId);
    scrollToMatch(matchId);
  }
}

/**
 * Set result for a match and move to next
 * @param matchId - The match ID
 * @param result - The result value
 */
async function setMatchResult(matchId: string, result: string): Promise<void> {
  const selectElement = document.getElementById(`result-${matchId}`) as HTMLSelectElement | null;
  if (!selectElement) return;

  // Check if this result option is available (e.g., draws not available in knockout)
  const option = selectElement.querySelector(`option[value="${result}"]`) as HTMLOptionElement | null;
  if (!option) {
    // Result not available for this format, ignore
    return;
  }

  // Set the value
  selectElement.value = result;

  // Submit the result
  await submitResult(matchId);

  // Move to next empty match
  selectNextMatch();
}

/**
 * Select the first match without a result
 * @param tournamentId - The tournament ID
 */
function selectFirstEmptyMatch(tournamentId: string): void {
  // Find all visible result selects without a value
  const pairingsTab = document.getElementById(`pairings-tab-${tournamentId}`);
  if (!pairingsTab) return;

  const selects = pairingsTab.querySelectorAll('select.result-select');
  for (const select of Array.from(selects)) {
    const selectElement = select as HTMLSelectElement;
    const matchId = selectElement.id.replace('result-', '');

    // Check if this match is visible (including checking parent round containers) and has no result
    const row = selectElement.closest('tr') as HTMLElement;
    if (row && isElementVisible(row) && !selectElement.value) {
      setActiveMatch(matchId);
      return;
    }
  }

  // No empty matches found
  currentActiveMatchId = null;
  removeAllActiveHighlights();
}

/**
 * Check if an element is visible (including checking parent containers)
 */
function isElementVisible(element: HTMLElement): boolean {
  // Check if the element itself is hidden
  if (element.style.display === 'none') return false;

  // Check if any parent round-content div is hidden
  const roundContent = element.closest('.round-content') as HTMLElement;
  if (roundContent && roundContent.style.display === 'none') return false;

  return true;
}

/**
 * Select the next match (preferring empty ones below, then wrapping to top, then allowing result override)
 */
function selectNextMatch(): void {
  if (!currentTournamentId) return;

  const pairingsTab = document.getElementById(`pairings-tab-${currentTournamentId}`);
  if (!pairingsTab) return;

  const selects = Array.from(pairingsTab.querySelectorAll('select.result-select')) as HTMLSelectElement[];

  // Find current match index
  let currentIndex = -1;
  if (currentActiveMatchId) {
    currentIndex = selects.findIndex(s => s.id === `result-${currentActiveMatchId}`);
  }

  // First, try to find next empty match BELOW the current one in visible rounds
  for (let i = currentIndex + 1; i < selects.length; i++) {
    const selectElement = selects[i]!;
    const matchId = selectElement.id.replace('result-', '');
    const row = selectElement.closest('tr') as HTMLElement;

    if (row && isElementVisible(row) && !selectElement.value) {
      setActiveMatch(matchId);
      scrollToMatch(matchId);
      return;
    }
  }

  // No empty matches found below - wrap around and find first empty match from the top
  for (let i = 0; i <= currentIndex; i++) {
    const selectElement = selects[i]!;
    const matchId = selectElement.id.replace('result-', '');
    const row = selectElement.closest('tr') as HTMLElement;

    if (row && isElementVisible(row) && !selectElement.value) {
      setActiveMatch(matchId);
      scrollToMatch(matchId);
      return;
    }
  }

  // No empty matches found - find next match with result BELOW current (allow override)
  for (let i = currentIndex + 1; i < selects.length; i++) {
    const selectElement = selects[i]!;
    const matchId = selectElement.id.replace('result-', '');
    const row = selectElement.closest('tr') as HTMLElement;

    if (row && isElementVisible(row)) {
      setActiveMatch(matchId);
      scrollToMatch(matchId);
      return;
    }
  }

  // Still nothing - wrap to find first match with result from top (allow override)
  for (let i = 0; i <= currentIndex; i++) {
    const selectElement = selects[i]!;
    const matchId = selectElement.id.replace('result-', '');
    const row = selectElement.closest('tr') as HTMLElement;

    if (row && isElementVisible(row)) {
      setActiveMatch(matchId);
      scrollToMatch(matchId);
      return;
    }
  }

  // No matches found at all - clear active match
  currentActiveMatchId = null;
  removeAllActiveHighlights();
}

/**
 * Select the previous match (preferring empty ones, but allowing override of completed ones)
 */
function selectPreviousMatch(): void {
  if (!currentTournamentId) return;

  const pairingsTab = document.getElementById(`pairings-tab-${currentTournamentId}`);
  if (!pairingsTab) return;

  const selects = Array.from(pairingsTab.querySelectorAll('select.result-select')) as HTMLSelectElement[];

  // Find current match index
  let currentIndex = selects.length;
  if (currentActiveMatchId) {
    currentIndex = selects.findIndex(s => s.id === `result-${currentActiveMatchId}`);
  }

  // First, try to find previous empty match in visible rounds
  for (let i = currentIndex - 1; i >= 0; i--) {
    const selectElement = selects[i]!;
    const matchId = selectElement.id.replace('result-', '');
    const row = selectElement.closest('tr') as HTMLElement;

    if (row && isElementVisible(row) && !selectElement.value) {
      setActiveMatch(matchId);
      scrollToMatch(matchId);
      return;
    }
  }

  // No empty matches found - select previous match with result (to allow override) in visible rounds
  for (let i = currentIndex - 1; i >= 0; i--) {
    const selectElement = selects[i]!;
    const matchId = selectElement.id.replace('result-', '');
    const row = selectElement.closest('tr') as HTMLElement;

    if (row && isElementVisible(row)) {
      setActiveMatch(matchId);
      scrollToMatch(matchId);
      return;
    }
  }

  // No previous visible match found, stay at current
}

/**
 * Set a match as active
 * @param matchId - The match ID
 */
function setActiveMatch(matchId: string): void {
  currentActiveMatchId = matchId;

  // Remove all existing highlights
  removeAllActiveHighlights();

  // Highlight the active row - try multiple methods to find it
  let row: HTMLElement | null = null;

  // Method 1: Find by select element
  const selectElement = document.getElementById(`result-${matchId}`);
  if (selectElement) {
    row = selectElement.closest('tr') as HTMLElement;
  }

  // Method 2: Find by data-match-id attribute on result cell
  if (!row) {
    const resultCell = document.querySelector(`td.result-cell[data-match-id="${matchId}"]`);
    if (resultCell) {
      row = resultCell.closest('tr') as HTMLElement;
    }
  }

  // Add the highlight class
  if (row) {
    row.classList.add('active-match');
  }
}

/**
 * Set active match when user clicks on a row
 * @param matchId - The match ID
 * @param tournamentId - The tournament ID
 */
export function setActiveMatchByClick(matchId: string, tournamentId: string): void {
  // Enable keyboard shortcuts for this tournament
  currentTournamentId = tournamentId;
  isKeyboardShortcutsEnabled = true;

  // Add keyboard event listener if not already added
  if (!document.body.hasAttribute('data-keyboard-shortcuts-initialized')) {
    document.addEventListener('keydown', handleKeyboardShortcut);
    document.body.setAttribute('data-keyboard-shortcuts-initialized', 'true');
  }

  // Set this match as active
  setActiveMatch(matchId);
}

/**
 * Remove all active match highlights
 */
function removeAllActiveHighlights(): void {
  document.querySelectorAll('tr.active-match').forEach(row => {
    row.classList.remove('active-match');
  });
}

/**
 * Scroll to a match row
 * @param matchId - The match ID
 */
function scrollToMatch(matchId: string): void {
  // Try multiple methods to find the row
  let row: HTMLElement | null = null;

  // Method 1: Find by select element
  const selectElement = document.getElementById(`result-${matchId}`);
  if (selectElement) {
    row = selectElement.closest('tr') as HTMLElement;
  }

  // Method 2: Find by data-match-id attribute on result cell
  if (!row) {
    const resultCell = document.querySelector(`td.result-cell[data-match-id="${matchId}"]`);
    if (resultCell) {
      row = resultCell.closest('tr') as HTMLElement;
    }
  }

  if (row) {
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

/**
 * Toggle visibility of completed results
 * @param tournamentId - The tournament ID
 * @param hideCompleted - Whether to hide completed results
 */
export function toggleCompletedResults(tournamentId: string, hideCompleted: boolean): void {
  const pairingsTab = document.getElementById(`pairings-tab-${tournamentId}`);
  if (!pairingsTab) return;

  const rows = pairingsTab.querySelectorAll('.matches-table tbody tr');

  rows.forEach(row => {
    const resultCell = row.querySelector('.result-cell') as HTMLElement;
    if (!resultCell) return;

    // Check if this match has a result
    const selectElement = resultCell.querySelector('select.result-select') as HTMLSelectElement | null;
    const hasResult = selectElement && selectElement.value !== '';

    // Also check for static result text (for byes or read-only results)
    const hasStaticResult = !selectElement && resultCell.textContent &&
                           resultCell.textContent.trim() !== '' &&
                           !resultCell.textContent.includes('Pending') &&
                           !resultCell.textContent.includes('pending');

    // Hide or show the row
    if (hideCompleted && (hasResult || hasStaticResult)) {
      (row as HTMLElement).style.display = 'none';
    } else {
      (row as HTMLElement).style.display = '';
    }
  });

  // After toggling, re-select the first empty match
  if (isKeyboardShortcutsEnabled) {
    selectFirstEmptyMatch(tournamentId);
  }
}

// Export to window for onclick handlers
window.initKeyboardShortcuts = initKeyboardShortcuts;
window.disableKeyboardShortcuts = disableKeyboardShortcuts;
window.toggleCompletedResults = toggleCompletedResults;
window.setActiveMatchByClick = setActiveMatchByClick;
